import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useConfigOverridePreview,
  useConfigOverridesSummary,
  useConfigOverridesView,
  useRevertConfigOverrides,
  useSetConfigOverridePath,
} from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { useHomebaseModal } from '../components/HomebaseModal';
import './ConfigUpdatesPage.css';

const PROFILE_LABELS = {
  soccer: 'Soccer',
  basketball: 'NCAAB / CBB',
  nba: 'NBA',
  wnba: 'WNBA',
  esports: 'Esports',
  nfl: 'NFL',
  ncaaf: 'NCAAF / CFB',
  mlb: 'MLB',
  nhl: 'NHL',
};

const DEFAULT_PROFILES = ['soccer', 'basketball', 'esports', 'nba', 'wnba', 'nfl', 'ncaaf', 'mlb', 'nhl'];

const MARGIN_LABELS = ['c0', 'cfav', 'cdog'];

const PARAMETER_GROUPS = [
  {
    id: 'timing',
    title: 'Engine timing',
    description: 'How often the writer advances KF / EntriesKF engines and related decay clocks.',
  },
  {
    id: 'coverage',
    title: 'Quote eligibility',
    description:
      'When a market can publish, two-sided gates, EntriesKF √P / dropout inject, and related risk limits.',
  },
  {
    id: 'weights',
    title: 'Book weights',
    description:
      'Per-sportsbook measurement variances (R) and uptime weights used by coverage gates.',
  },
  {
    id: 'pricing',
    title: 'Pricing & margin',
    description: 'Controls how uncertainty is translated into customer-facing margin.',
  },
  {
    id: 'alternatives',
    title: 'Alternative lines',
    description: 'Availability and risk limits for non-base lines.',
  },
  {
    id: 'lifecycle',
    title: 'Market lifecycle',
    description: 'Controls whether and when this market is available around fixture start.',
  },
  {
    id: 'baseline',
    title: 'Baseline movement',
    description:
      'Controls whether the main line follows the predictive median or an active book reference, and how long a move must persist.',
  },
  {
    id: 'panel',
    title: 'Uncertainty Calibration',
    description:
      ' Knobs to set higher/lower uncertainty on markets',
  },
  {
    id: 'order_book',
    title: 'Order-book translation',
    description: 'Variance and edge assumptions used to translate between probability and depth.',
  },
  {
    id: 'model',
    title: 'Model seed',
    description:
      'Model feed controls: enable/disable, waive min-books, mute competitors, and debounce.',
  },
  {
    id: 'relationships',
    title: 'Parent relationships',
    description:
      'Model-feed watchers and derived-market inputs. Only shown for markets that already have a model feed or derived rule.',
  },
];

function formatApiError(err) {
  const d = err?.response?.data;
  if (typeof d?.detail === 'string') return d.detail;
  if (Array.isArray(d?.detail)) {
    return d.detail.map((x) => x.msg || JSON.stringify(x)).join('; ');
  }
  return err?.message || 'Request failed';
}

function formatTs(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function formatVal(val) {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  if (Array.isArray(val)) {
    if (val.some((item) => item !== null && typeof item === 'object')) {
      return JSON.stringify(val);
    }
    return val.join(', ');
  }
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

function formatComparisonVal(val) {
  if (Array.isArray(val)) return `${val.length} tier${val.length === 1 ? '' : 's'}`;
  if (val && typeof val === 'object') return 'configured';
  return formatVal(val);
}

function formatDisplayVal(row, val) {
  if (row.type === 'json' && val != null) {
    return JSON.stringify(val, null, 2);
  }
  return formatVal(val);
}

function configTooltip(item) {
  return [item?.description, item?.operator_hint ? `Operator effect: ${item.operator_hint}` : null]
    .filter(Boolean)
    .join('\n\n');
}

function draftForRow(row) {
  const v = row.is_overridden ? row.override_value : row.effective_value;
  if (row.type === 'bool') return v === true || v === 'true' ? 'true' : 'false';
  if (row.type === 'margin_triplet' && Array.isArray(v)) return v.join(', ');
  if (row.type === 'json') return v == null ? '' : JSON.stringify(v, null, 2);
  return v === null || v === undefined ? '' : String(v);
}

/** Discrete catalog stances only (custom write-ins stay off the toggle). */
function stanceOptionsForRow(row) {
  const numeric = (Array.isArray(row.options) ? row.options : [])
    .map((opt) => Number(opt))
    .filter((opt) => Number.isFinite(opt));
  // Always low → high so effect labels match the numeric scale.
  return [...new Set(numeric)].sort((a, b) => a - b);
}

function formatStanceOption(opt) {
  const n = Number(opt);
  if (!Number.isFinite(n)) return String(opt);
  if (Number.isInteger(n)) return String(n);
  return String(Number(n.toPrecision(6)));
}

function isCatalogStanceValue(row, raw) {
  if (String(raw ?? '').trim() === '') return false;
  const n = Number(raw);
  if (!Number.isFinite(n)) return false;
  return (Array.isArray(row.options) ? row.options : []).some((opt) => Number(opt) === n);
}

function StanceToggle({ row, value, onChange }) {
  const options = stanceOptionsForRow(row);
  const selected = value !== '' && value != null ? Number(value) : NaN;
  const customActive = String(value ?? '').trim() !== '' && !isCatalogStanceValue(row, value);
  const leftEffect = row.stance_left_effect || 'More conservative';
  const rightEffect = row.stance_right_effect || 'More aggressive';

  return (
    <div className="config-updates-stance">
      <div className="config-updates-stance-guide">
        <span className="config-updates-stance-end">
          <span aria-hidden="true">←</span>
          <span className="config-updates-stance-effect">{leftEffect}</span>
        </span>
        <span className="config-updates-stance-end config-updates-stance-end--right">
          <span className="config-updates-stance-effect">{rightEffect}</span>
          <span aria-hidden="true">→</span>
        </span>
      </div>
      <div
        className="config-updates-stance-toggle"
        role="radiogroup"
        aria-label={`${row.label} stance`}
      >
        {options.map((opt) => {
          const active = Number.isFinite(selected) && selected === opt;
          return (
            <button
              key={String(opt)}
              type="button"
              role="radio"
              aria-checked={active}
              className={`config-updates-stance-option${active ? ' is-active' : ''}`}
              onClick={() => onChange(String(opt))}
            >
              {formatStanceOption(opt)}
            </button>
          );
        })}
      </div>
      <label className={`config-updates-writein${customActive ? ' is-custom' : ''}`}>
        <span>Custom</span>
        <input
          type="number"
          className="filter-input config-updates-writein-input"
          value={value ?? ''}
          min={row.min}
          max={row.max}
          step={row.step ?? 'any'}
          placeholder="override"
          title="Optional custom value for operators who need a value outside the stance scale"
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    </div>
  );
}

function parseValue(row, raw) {
  if (row.type === 'bool') {
    return ['1', 'true', 'yes', 'on'].includes(String(raw).trim().toLowerCase());
  }
  if (row.type === 'int') {
    if (String(raw).trim() === '') throw new Error(`Enter a value for ${row.label}`);
    const n = Number(raw);
    if (!Number.isInteger(n)) throw new Error(`Invalid integer for ${row.label}`);
    if (row.min != null && n < row.min) throw new Error(`${row.label} must be at least ${row.min}`);
    if (row.max != null && n > row.max) throw new Error(`${row.label} must be at most ${row.max}`);
    return n;
  }
  if (row.type === 'float') {
    if (String(raw).trim() === '') throw new Error(`Enter a value for ${row.label}`);
    const n = Number(raw);
    if (!Number.isFinite(n)) throw new Error(`Invalid number for ${row.label}`);
    if (row.min != null && n < row.min) throw new Error(`${row.label} must be at least ${row.min}`);
    if (row.max != null && n > row.max) throw new Error(`${row.label} must be at most ${row.max}`);
    return n;
  }
  if (row.type === 'margin_triplet') {
    const parts = String(raw)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length !== 3) {
      throw new Error('Margin triplet must be three comma-separated numbers: c0, cfav, cdog');
    }
    const values = parts.map((p) => Number(p));
    if (values.some((value) => !Number.isFinite(value))) {
      throw new Error('Every assumed-margin value must be a number');
    }
    return values;
  }
  if (row.type === 'json') {
    if (String(raw).trim() === '') throw new Error(`Enter JSON for ${row.label}`);
    try {
      return JSON.parse(raw);
    } catch {
      throw new Error(`Invalid JSON for ${row.label}`);
    }
  }
  return raw;
}

function valuesEqual(left, right) {
  if (Array.isArray(left) || Array.isArray(right)) {
    return JSON.stringify(left) === JSON.stringify(right);
  }
  return left === right;
}

function draftChanged(row, raw) {
  try {
    return !valuesEqual(parseValue(row, raw), row.effective_value);
  } catch {
    return false;
  }
}

function isLogCiCutoff(row) {
  return row.field === 'log_ci_cutoff' || row.field === 'alts_log_ci_cutoff';
}

function formatFiftyFiftyCiPreview(raw) {
  if (String(raw ?? '').trim() === '') return null;
  const logitWidth = Number(raw);
  if (!Number.isFinite(logitWidth) || logitWidth < 0) return null;
  const probabilityWidth = 2 / (1 + Math.exp(-logitWidth / 2)) - 1;
  return `${(probabilityWidth * 100).toFixed(1)}pp`;
}

function ImpactPreview({ profile, path, newValue, oldValue, marketId, enabled }) {
  const { data, isFetching, isError } = useConfigOverridePreview(
    { profile, path, newValue, oldValue, marketId },
    { enabled: Boolean(enabled && profile && path && String(newValue ?? '').trim() !== '') }
  );
  if (!enabled) return null;
  if (isFetching && !data) {
    return <div className="config-updates-impact-preview subtle">Estimating impact…</div>;
  }
  if (isError || !data?.supported) return null;
  return (
    <div className="config-updates-impact-preview" title="Approximate counterfactual from live quotes / recent signals">
      Impact ≈ {data.summary}
    </div>
  );
}

function MarginTripletInput({ value, onChange }) {
  const parts = String(value ?? '').split(',').map((part) => part.trim());
  const updatePart = (index, nextValue) => {
    const next = MARGIN_LABELS.map((_, partIndex) => (partIndex === index ? nextValue : parts[partIndex] || ''));
    onChange(next.join(', '));
  };

  return (
    <div className="config-updates-margin-input">
      {MARGIN_LABELS.map((label, index) => (
        <label key={label}>
          <span>{label}</span>
          <input
            type="number"
            step="any"
            className="filter-input"
            value={parts[index] || ''}
            onChange={(event) => updatePart(index, event.target.value)}
          />
        </label>
      ))}
    </div>
  );
}

function ConfigRowTable({
  title,
  description,
  rows,
  drafts,
  onDraftChange,
  onApply,
  onRevert,
  busy,
  previewProfile,
  previewMarketId,
}) {
  const [expanded, setExpanded] = useState(false);
  if (!rows?.length) return null;
  return (
    <div className={`config-updates-section-block${expanded ? ' config-updates-section-block--expanded' : ''}`}>
      <div className="config-updates-section-heading">
        <button
          type="button"
          className="config-updates-section-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          <span className="config-updates-section-caret" aria-hidden="true">
            {expanded ? '▾' : '▸'}
          </span>
          <span>
            <span className="config-updates-subsection">{title}</span>
            {description ? (
              <span className="subtle config-updates-section-description" title={description}>
                {description}
              </span>
            ) : null}
          </span>
        </button>
      </div>
      {expanded ? <div className="config-updates-table-scroll">
        <table className="table config-updates-table">
          <thead>
            <tr>
              <th>Setting</th>
              <th>Config default</th>
              <th>Current</th>
              <th>New value</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const changed = draftChanged(row, drafts[row.path]);
              const fiftyFiftyCiPreview = isLogCiCutoff(row)
                ? formatFiftyFiftyCiPreview(drafts[row.path])
                : null;
              return (
                <tr key={row.path} className={row.is_overridden ? 'config-updates-row--active' : ''}>
                  <td>
                    <div className="config-updates-label" title={configTooltip(row)}>
                      {row.label}
                    </div>
                    {row.description ? (
                      <div className="config-updates-description" title={configTooltip(row)}>
                        {row.description}
                      </div>
                    ) : null}
                    <code className="config-updates-path">{row.path}</code>
                  </td>
                  <td className="mono">
                    <div className={row.type === 'json' ? 'config-updates-json-preview' : undefined}>
                      {formatDisplayVal(row, row.yaml_value)}
                    </div>
                    {row.yaml_source ? (
                      <span className="config-updates-source">
                        {row.yaml_source}
                      </span>
                    ) : null}
                  </td>
                  <td className="mono">
                    <div className={row.type === 'json' ? 'config-updates-json-preview' : undefined}>
                      {formatDisplayVal(row, row.effective_value)}
                      {row.unit && row.effective_value != null ? <span className="config-updates-unit"> {row.unit}</span> : null}
                    </div>
                    {row.effective_source ? (
                      <span className={`config-updates-source${row.is_overridden ? ' config-updates-source--override' : ''}`}>
                        {row.effective_source}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    {row.type === 'bool' ? (
                      <select
                        className="filter-select config-updates-cell-input"
                        value={drafts[row.path] ?? ''}
                        onChange={(e) => onDraftChange(row.path, e.target.value)}
                      >
                        <option value="true">Enabled</option>
                        <option value="false">Disabled</option>
                      </select>
                    ) : row.type === 'baseline_mode' ? (
                      <select
                        className="filter-select config-updates-cell-input"
                        value={drafts[row.path] ?? ''}
                        onChange={(e) => onDraftChange(row.path, e.target.value)}
                      >
                        {(row.options || []).map((option) => (
                          <option key={String(option)} value={String(option)}>
                            {String(option)}
                          </option>
                        ))}
                      </select>
                    ) : row.type === 'margin_triplet' ? (
                      <MarginTripletInput
                        value={drafts[row.path]}
                        onChange={(value) => onDraftChange(row.path, value)}
                      />
                    ) : row.type === 'json' ? (
                      <textarea
                        className="filter-input config-updates-cell-input config-updates-json-input"
                        value={drafts[row.path] ?? ''}
                        rows={row.group === 'weights' || row.field?.endsWith('.parents') ? 12 : 5}
                        spellCheck={false}
                        onChange={(e) => onDraftChange(row.path, e.target.value)}
                      />
                    ) : Array.isArray(row.options) && row.options.length > 0 ? (
                      <>
                        <StanceToggle
                          row={row}
                          value={drafts[row.path] ?? ''}
                          onChange={(next) => onDraftChange(row.path, next)}
                        />
                        {fiftyFiftyCiPreview ? (
                          <div
                            className="config-updates-ci-preview"
                            title="Probability-width equivalent for a confidence interval centered at 50%. The equivalent width is smaller in the tails."
                          >
                            At 50/50 ≈ <strong>{fiftyFiftyCiPreview}</strong> CI width
                          </div>
                        ) : null}
                        <ImpactPreview
                          profile={previewProfile}
                          path={row.path}
                          newValue={drafts[row.path]}
                          oldValue={row.effective_value}
                          marketId={previewMarketId}
                          enabled={changed}
                        />
                      </>
                    ) : (
                      <>
                        <div className="config-updates-writein-wrap">
                          <input
                            type="number"
                            className="filter-input config-updates-writein-input"
                            value={drafts[row.path] ?? ''}
                            min={row.min}
                            max={row.max}
                            step={row.step ?? 'any'}
                            onChange={(e) => onDraftChange(row.path, e.target.value)}
                          />
                        </div>
                        {fiftyFiftyCiPreview ? (
                          <div
                            className="config-updates-ci-preview"
                            title="Probability-width equivalent for a confidence interval centered at 50%. The equivalent width is smaller in the tails."
                          >
                            At 50/50 ≈ <strong>{fiftyFiftyCiPreview}</strong> CI width
                          </div>
                        ) : null}
                        <ImpactPreview
                          profile={previewProfile}
                          path={row.path}
                          newValue={drafts[row.path]}
                          oldValue={row.effective_value}
                          marketId={previewMarketId}
                          enabled={changed}
                        />
                      </>
                    )}
                  </td>
                  <td className="config-updates-row-actions">
                    <button
                      type="button"
                      className="button button-small"
                      disabled={busy || !changed}
                      onClick={() => onApply(row)}
                    >
                      Apply
                    </button>
                    {row.is_overridden ? (
                      <button
                        type="button"
                        className="button button-small"
                        disabled={busy}
                        onClick={() => onRevert(row.path)}
                        title="Remove this override and resume using the inherited config value"
                      >
                        Reset
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div> : null}
    </div>
  );
}

function MarketComparisonCheatSheet({ marketId, comparison }) {
  const fields = useMemo(() => comparison?.fields || [], [comparison?.fields]);
  const rows = useMemo(() => comparison?.rows || [], [comparison?.rows]);
  const [groupId, setGroupId] = useState('coverage');

  useEffect(() => {
    if (fields.length && !fields.some((item) => item.group === groupId)) {
      setGroupId(fields[0].group);
    }
  }, [fields, groupId]);

  const availableGroups = PARAMETER_GROUPS.map((group) => ({
    ...group,
    fields: fields.filter((item) => item.group === group.id),
  })).filter((group) => group.fields.length);
  const selectedGroup = availableGroups.find((group) => group.id === groupId);
  const selectedFields = selectedGroup?.fields || [];

  return (
    <div className="card config-updates-comparison">
      <div className="config-updates-comparison-header">
        <div className="config-updates-comparison-heading">
          <span>
            <span className="config-updates-eyebrow">Cheat sheet</span>
            <span className="config-updates-comparison-title">Compare values across markets</span>
          </span>
        </div>
        <select
          className="filter-select config-updates-comparison-type"
          value={groupId}
          aria-label="Config type"
          title="Config type"
          onChange={(event) => setGroupId(event.target.value)}
        >
          {availableGroups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.title}
            </option>
          ))}
        </select>
      </div>
      <div className="config-updates-comparison-body">
        <div className="config-updates-comparison-scroll">
          <table className="table config-updates-comparison-table">
              <thead>
                <tr>
                  <th>Market</th>
                  {selectedFields.map((item) => (
                    <th key={item.field} title={configTooltip(item) || item.label}>
                      {item.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  return (
                    <tr
                      key={row.market_id}
                      className={
                        row.market_id === marketId
                          ? 'config-updates-comparison-row--selected'
                          : ''
                      }
                    >
                      <td>
                        <span className="config-updates-label">{row.market_id}</span>
                      </td>
                      {selectedFields.map((item) => {
                        const value = row.values?.[item.field] || {};
                        const source = `${value.source || 'unset'}${
                          value.is_overridden ? ' · override' : ''
                        }`;
                        return (
                          <td
                            key={item.field}
                            className={`mono${
                              value.is_overridden ? ' config-updates-comparison-cell--override' : ''
                            }`}
                            title={`${item.label}: ${source}`}
                        >
                            {formatComparisonVal(value.value)}
                            {item.unit && value.value != null ? (
                              <span className="config-updates-unit"> {item.unit}</span>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function ConfigUpdatesPage() {
  const { user } = useAuth();
  const { confirm, ModalLayer } = useHomebaseModal();

  const [profile, setProfile] = useState('soccer');
  const [marketId, setMarketId] = useState('');
  const [leagueId, setLeagueId] = useState('');
  const [configScope, setConfigScope] = useState('sport'); // 'sport' | 'market'
  const [reason, setReason] = useState('');
  const [fanoutWriters, setFanoutWriters] = useState(true);
  const [lastError, setLastError] = useState(null);
  const [lastSuccess, setLastSuccess] = useState(null);
  const [drafts, setDrafts] = useState({});

  const { data: summary } = useConfigOverridesSummary();
  const { data: view, isLoading, refetch } = useConfigOverridesView(profile, marketId);
  const setOverride = useSetConfigOverridePath();
  const revertOverride = useRevertConfigOverrides();

  const profiles = useMemo(() => {
    const fromApi = Array.isArray(summary?.profiles) ? summary.profiles : null;
    return fromApi?.length ? fromApi : DEFAULT_PROFILES;
  }, [summary?.profiles]);
  const marketIds = useMemo(() => view?.market_ids || [], [view?.market_ids]);
  const market = view?.market;
  const writerRows = useMemo(() => view?.writer_rows || [], [view?.writer_rows]);
  const activeRows = view?.rows || [];
  const writerGroups = useMemo(
    () =>
      PARAMETER_GROUPS.map((group) => ({
        ...group,
        rows: writerRows.filter((row) => row.group === group.id),
      })).filter((group) => group.rows.length),
    [writerRows]
  );
  const scalarGroups = useMemo(
    () =>
      PARAMETER_GROUPS.map((group) => ({
        ...group,
        rows: (market?.scalar_rows || []).filter((row) => row.group === group.id),
      })).filter((group) => group.rows.length),
    [market?.scalar_rows]
  );
  const leagueGroups = useMemo(() => {
    const grouped = new Map();
    for (const row of market?.league_rows || []) {
      if (!grouped.has(row.league)) grouped.set(row.league, []);
      grouped.get(row.league).push(row);
    }
    return Array.from(grouped, ([league, rows]) => ({ league, rows }));
  }, [market?.league_rows]);
  const selectedLeague = leagueGroups.find(({ league }) => league === leagueId) || leagueGroups[0];

  useEffect(() => {
    if (!marketId && marketIds.length) {
      setMarketId(marketIds[0]);
    }
  }, [marketId, marketIds]);

  useEffect(() => {
    if (!leagueGroups.length) {
      setLeagueId('');
    } else if (!leagueGroups.some(({ league }) => league === leagueId)) {
      setLeagueId(leagueGroups[0].league);
    }
  }, [leagueGroups, leagueId]);

  useEffect(() => {
    if (!market) {
      setDrafts({});
      return;
    }
    const all = [
      ...writerRows,
      ...(market.scalar_rows || []),
      ...(market.margin_rows || []),
      ...(market.league_rows || []),
    ];
    const next = {};
    for (const row of all) {
      next[row.path] = draftForRow(row);
    }
    setDrafts(next);
  }, [market, writerRows]);

  const instanceProfile = summary?.instance_profile || view?.instance_profile;
  const meta = view?.meta || {};
  const busy = setOverride.isPending || revertOverride.isPending;

  const onDraftChange = useCallback((path, value) => {
    setDrafts((prev) => ({ ...prev, [path]: value }));
  }, []);

  const applyRow = useCallback(
    async (row) => {
      setLastError(null);
      setLastSuccess(null);
      const raw = drafts[row.path];
      let parsed;
      try {
        parsed = parseValue(row, raw);
      } catch (err) {
        setLastError(err.message);
        return;
      }
      const ok = await confirm({
        title: 'Save config value?',
        body: `${row.path} → ${formatVal(parsed)}`,
      });
      if (!ok) return;
      try {
        const result = await setOverride.mutateAsync({
          profile,
          path: row.path,
          value: parsed,
          reason: reason.trim() || undefined,
          updated_by: user?.email || 'dashboard',
          fanout_writers: fanoutWriters,
        });
        setLastSuccess(`Saved ${row.label} (mode: ${result?.mode || 'ok'})`);
        await refetch();
      } catch (err) {
        setLastError(formatApiError(err));
      }
    },
    [confirm, drafts, fanoutWriters, profile, reason, refetch, setOverride, user?.email]
  );

  const revertPath = useCallback(
    async (path) => {
      const ok = await confirm({
        title: 'Reset to config default?',
        body: `Remove the override for ${path} and resume using its inherited config value.`,
      });
      if (!ok) return;
      setLastError(null);
      try {
        await revertOverride.mutateAsync({
          profile,
          paths: [path],
          reason: 'dashboard revert',
          updated_by: user?.email || 'dashboard',
          fanout_writers: fanoutWriters,
        });
        setLastSuccess(`Reverted ${path}`);
        await refetch();
      } catch (err) {
        setLastError(formatApiError(err));
      }
    },
    [confirm, fanoutWriters, profile, refetch, revertOverride, user?.email]
  );

  const clearAll = useCallback(async () => {
    const ok = await confirm({
      title: 'Reset all to config defaults?',
      body: `Remove ALL changed values for ${PROFILE_LABELS[profile] || profile} and resume using inherited config values.`,
      danger: true,
    });
    if (!ok) return;
    setLastError(null);
    try {
      await revertOverride.mutateAsync({
        profile,
        clear_all: true,
        reason: 'dashboard clear all',
        updated_by: user?.email || 'dashboard',
        fanout_writers: fanoutWriters,
      });
      setLastSuccess('All overrides cleared');
      await refetch();
    } catch (err) {
      setLastError(formatApiError(err));
    }
  }, [confirm, fanoutWriters, profile, refetch, revertOverride, user?.email]);

  return (
    <div className="config-updates-page">
      <ModalLayer />
      <div className="page-header">
        <div>
          <h1>Config updates</h1>
          <p className="subtle config-updates-lede">
            Live configuration for market margination, book gates, order-book translation, and league
            specificity. Codebase configuration resolves from base to sport to market to league. Only
            explicit dashboard changes are stored in Postgres and hot-applied on sport writers.
          </p>
        </div>
        <div className="config-updates-header-actions">
          <button type="button" className="button" onClick={() => refetch()} disabled={isLoading}>
            Refresh
          </button>
        </div>
      </div>

      <div className="card config-updates-profile-bar">
        <span className="subtle">Writer profile</span>
        <div className="config-updates-profile-tabs" role="tablist">
          {profiles.map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={profile === p}
              className={`config-updates-profile-tab${profile === p ? ' config-updates-profile-tab--active' : ''}`}
              onClick={() => {
                setProfile(p);
                setMarketId('');
                setLeagueId('');
              }}
            >
              {PROFILE_LABELS[p] || p}
            </button>
          ))}
        </div>
        <label className="config-updates-market-select">
          <span className="subtle">Market type</span>
          <select
            className="filter-select"
            value={marketId}
            onChange={(e) => setMarketId(e.target.value)}
            disabled={!marketIds.length}
          >
            {marketIds.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        </label>
        {instanceProfile ? (
          <p className="subtle config-updates-instance">
            This API instance: <code>{instanceProfile}</code>
            {instanceProfile === profile ? ' (local apply)' : ' (fan-out to writer)'}
          </p>
        ) : null}
      </div>

      <div className="config-updates-status-row">
        <div className="card config-updates-meta">
          <div className="config-updates-meta-grid">
            <div>
              <span className="subtle">Override version</span>
              <strong>{meta.version ?? 0}</strong>
            </div>
            <div>
              <span className="subtle">Last updated</span>
              <strong>{formatTs(meta.updated_at)}</strong>
            </div>
            <div>
              <span className="subtle">Updated by</span>
              <strong>{meta.updated_by || '—'}</strong>
            </div>
            <div>
              <span className="subtle">Active overrides</span>
              <strong>{activeRows.length}</strong>
            </div>
          </div>
          <div className="config-updates-apply-row" style={{ marginTop: '0.75rem' }}>
            <label>
              Reason (optional)
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="filter-input"
                placeholder="e.g. tighter EPL gates"
              />
            </label>
            <label className="config-updates-checkbox">
              <input
                type="checkbox"
                checked={fanoutWriters}
                onChange={(e) => setFanoutWriters(e.target.checked)}
              />
              Fan out to writer
            </label>
          </div>
        </div>
        <MarketComparisonCheatSheet
          marketId={marketId}
          comparison={view?.market_comparison}
        />
      </div>

      {(lastError || lastSuccess) && (
        <div className={`card config-updates-flash${lastError ? ' config-updates-flash--err' : ''}`}>
          {lastError || lastSuccess}
        </div>
      )}

      <div className="card config-updates-editor">
        <div className="config-updates-editor-heading">
          <div>
            <span className="config-updates-eyebrow">Configuration</span>
            <h2>
              {configScope === 'sport'
                ? `${PROFILE_LABELS[profile] || profile} defaults`
                : marketId || 'Market'}
            </h2>
          </div>
          <div className="config-updates-scope-toggle" role="group" aria-label="Config scope">
            <button
              type="button"
              className={`config-updates-scope-tab${configScope === 'sport' ? ' config-updates-scope-tab--active' : ''}`}
              onClick={() => setConfigScope('sport')}
            >
              Sport
            </button>
            <button
              type="button"
              className={`config-updates-scope-tab${configScope === 'market' ? ' config-updates-scope-tab--active' : ''}`}
              onClick={() => setConfigScope('market')}
            >
              Market
            </button>
          </div>
        </div>
        <p className="subtle config-updates-scope-hint">
          {configScope === 'sport'
            ? 'Edit writer-wide defaults. Markets inherit these unless overridden.'
            : 'Edit this market only. Unset fields inherit the sport default.'}
        </p>

        {configScope === 'sport' ? (
          isLoading && !writerGroups.length ? (
            <div className="loading" style={{ padding: '1.5rem' }}>Loading…</div>
          ) : (
            writerGroups.map((group) => (
              <ConfigRowTable
                key={group.id}
                title={group.title}
                description={group.description}
                rows={group.rows}
                drafts={drafts}
                onDraftChange={onDraftChange}
                onApply={applyRow}
                onRevert={revertPath}
                busy={busy}
                previewProfile={profile}
                previewMarketId={null}
              />
            ))
          )
        ) : isLoading && !market ? (
          <div className="loading" style={{ padding: '1.5rem' }}>Loading…</div>
        ) : market ? (
          <>
            {scalarGroups.map((group) => (
              <ConfigRowTable
                key={group.id}
                title={group.title}
                description={group.description}
                rows={group.rows}
                drafts={drafts}
                onDraftChange={onDraftChange}
                onApply={applyRow}
                onRevert={revertPath}
                busy={busy}
                previewProfile={profile}
                previewMarketId={marketId}
              />
            ))}
            {(market.margin_rows || []).length ? (
              <ConfigRowTable
                title="Assumed margins"
                description="Per-book one-sided demargination triplets for this market."
                rows={market.margin_rows}
                drafts={drafts}
                onDraftChange={onDraftChange}
                onApply={applyRow}
                onRevert={revertPath}
                busy={busy}
                previewProfile={profile}
                previewMarketId={marketId}
              />
            ) : null}
            {leagueGroups.length ? (
              <div className="config-updates-league-block">
                <div className="config-updates-league-header">
                  <span className="config-updates-subsection">League overlays</span>
                  <select
                    className="filter-select"
                    value={selectedLeague?.league || ''}
                    onChange={(e) => setLeagueId(e.target.value)}
                  >
                    {leagueGroups.map(({ league }) => (
                      <option key={league} value={league}>
                        {league}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedLeague ? (
                  <ConfigRowTable
                    title={selectedLeague.league}
                    description="League-specific overrides on top of this market."
                    rows={selectedLeague.rows}
                    drafts={drafts}
                    onDraftChange={onDraftChange}
                    onApply={applyRow}
                    onRevert={revertPath}
                    busy={busy}
                    previewProfile={profile}
                    previewMarketId={marketId}
                  />
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <div className="subtle" style={{ padding: '1rem' }}>Select a market to edit.</div>
        )}
      </div>

      <div className="card">
        <div className="config-updates-table-header">
          <h2 className="config-updates-section-title">Explicit overrides</h2>
          <button
            type="button"
            className="button button-small button-danger"
            disabled={busy || activeRows.length === 0}
            onClick={clearAll}
            title="Remove every explicit override for this profile"
          >
            Reset all to config defaults
          </button>
        </div>
        {activeRows.length === 0 ? (
          <p className="subtle" style={{ padding: '0 0 1rem' }}>
            No explicit overrides — all current values come from the merged profile configuration.
          </p>
        ) : (
          <table className="table config-updates-table">
            <thead>
              <tr>
                <th>Path</th>
                <th>Current</th>
                <th>Config default</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {activeRows.map((row) => (
                <tr key={row.path} className="config-updates-row--active">
                  <td>
                    <code className="config-updates-path">{row.path}</code>
                  </td>
                  <td className="mono">{formatVal(row.override_value)}</td>
                  <td className="mono">{formatVal(row.yaml_value)}</td>
                  <td>
                    <button
                      type="button"
                      className="button button-small"
                      disabled={busy}
                      onClick={() => revertPath(row.path)}
                      title="Remove this override and resume using the inherited config value"
                    >
                      Reset
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
